import React, { useEffect, useState } from "react";
import { Play, X } from "lucide-react";
import { Reveal } from "./Reveal";
import api from "../../lib/api";

export const Highlights = () => {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/highlights").then((res) => setItems(res.data)).catch(() => {});
  }, []);

  return (
    <section id="highlights" data-testid="highlights-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-matte">
      <Reveal>
        <p className="eyebrow mb-4">The Film Room</p>
        <h2 className="heading text-5xl sm:text-6xl lg:text-7xl text-cream mb-3">The Tape Doesn't Lie.</h2>
        <p className="text-cream/60 max-w-md mb-14">The work. The moments. The journey.</p>
      </Reveal>

      {items.length === 0 ? (
        <p className="text-cream/40">No highlights yet.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((h, i) => (
            <Reveal key={h.id} delay={(i % 3) * 0.08}>
              <button
                data-testid={`highlight-${i}`}
                onClick={() => setActive(h)}
                className={`group relative w-full overflow-hidden block text-left ${
                  i % 5 === 0 ? "lg:col-span-2 aspect-[2/1]" : "aspect-video"
                }`}
              >
                <img
                  src={h.thumbnail}
                  alt={h.title}
                  className="absolute inset-0 w-full h-full object-cover film-warm transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-matte via-matte/30 to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="w-14 h-14 rounded-full bg-stone/90 flex items-center justify-center">
                    <Play className="text-matte ml-1" size={22} fill="currentColor" />
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 p-5">
                  <span className="text-[0.6rem] uppercase tracking-[0.25em] text-stone">{h.category}</span>
                  <h3 className="heading text-2xl text-cream mt-1">{h.title}</h3>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      )}

      {active && (
        <div
          data-testid="highlight-lightbox"
          className="fixed inset-0 z-[70] bg-matte/95 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setActive(null)}
        >
          <button
            data-testid="lightbox-close"
            className="absolute top-6 right-6 text-cream hover:text-stone"
            onClick={() => setActive(null)}
          >
            <X size={32} />
          </button>
          <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div
              className={`w-full bg-black mx-auto ${
                active.orientation === "portrait" ? "aspect-[9/16] max-w-[400px]" : "aspect-video"
              }`}
            >
              {active.platform === "video" ? (
                <>
                  <video
                    poster={active.thumbnail || undefined}
                    controls
                    autoPlay
                    muted
                    playsInline
                    className="w-full h-full object-contain bg-black"
                    onError={(e) => {
                      // Only show fallback if the VIDEO itself failed (all sources exhausted),
                      // not when an individual <source> (e.g. mp4 in a codec-less browser) errors.
                      if (e.target.tagName !== "VIDEO") return;
                      e.currentTarget.style.display = "none";
                      const msg = e.currentTarget.nextElementSibling;
                      if (msg) msg.style.display = "flex";
                    }}
                  >
                    <source src={active.embed_url} type="video/mp4" />
                    {active.embed_url.startsWith("/highlights/") && active.embed_url.endsWith(".mp4") && (
                      <source src={active.embed_url.replace(/\.mp4$/, ".webm")} type="video/webm" />
                    )}
                  </video>
                  <div style={{ display: "none" }} className="w-full h-full items-center justify-center text-silver/60 text-sm text-center px-6">
                    This video can't be played in your browser. Try Chrome or Safari.
                  </div>
                </>
              ) : (
                <iframe
                  title={active.title}
                  src={active.embed_url}
                  className="w-full h-full"
                  scrolling="no"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              )}
            </div>
            <div className="mt-4">
              <span className="text-[0.6rem] uppercase tracking-[0.25em] text-stone">{active.category}</span>
              <h3 className="heading text-3xl text-cream">{active.title}</h3>
              {active.description && <p className="text-cream/60 mt-1">{active.description}</p>}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
